import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { LEVEL_CONFIG, getLevelConfig, getLevelName, getReadingStage } from '../../config/levelConfig.js';
import { tapBounce } from '../../lib/motionPresets.js';

function getJourneyGroups() {
  const groups = [];
  for (const item of LEVEL_CONFIG) {
    const last = groups[groups.length - 1];
    if (!last || last.name !== item.name) {
      groups.push({ name: item.name, minLevel: item.level, maxLevel: item.level });
    } else {
      last.maxLevel = item.level;
    }
  }
  return groups;
}

function groupColor(maxLevel) {
  if (maxLevel <= 6) return '#F59E0B';
  if (maxLevel <= 12) return '#14B8A6';
  return '#16A34A';
}

export function PlacementResults({ level, onContinue, ctaMode = 'start' }) {
  const [beat2, setBeat2] = useState(false);
  const [beat3, setBeat3] = useState(false);
  const [beat4, setBeat4] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const groups = useMemo(() => getJourneyGroups(), []);

  useEffect(() => {
    const t2 = setTimeout(() => setBeat2(true), 1500);
    const t3 = setTimeout(() => setBeat3(true), 3000);
    const t4 = setTimeout(() => setBeat4(true), 4500);
    return () => {
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);

  const levelName = getLevelName(level);
  const levelConfig = getLevelConfig(level);
  const stage = getReadingStage(level);
  const nextGroup = groups.find((g) => g.minLevel > level);

  return (
    <motion.div className="px-6 py-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}>
      <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }} className="mb-5 text-center">
        <div className="mb-2 text-6xl">✨</div>
        <h2 className="font-finley-display text-5xl text-finley-text">Adventure Found!</h2>
      </motion.div>

      {beat2 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mb-5 rounded-finley-xl border-2 border-finley-primary/20 bg-finley-primary/5 p-5 text-center shadow-finley-sm">
          <div className="mb-2 font-finley-display text-3xl text-finley-primary">Your child is a {levelName}!</div>
          <p className="m-0 text-sm leading-6 text-finley-text-light">
            <strong className="text-finley-text">{stage?.stageName || 'Reading Stage'}:</strong> {stage?.description || 'Finley is tuning to your child in real time.'}
          </p>
        </motion.div>
      )}

      {beat3 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mb-5 rounded-finley-xl bg-finley-bg-card p-5 shadow-finley-md">
          <div className="mb-3 font-finley-display text-xl text-finley-text">Journey Map</div>
          <div className="space-y-3">
            {groups.map((g) => {
              const completed = level >= g.maxLevel;
              const current = level >= g.minLevel && level <= g.maxLevel;
              const color = groupColor(g.maxLevel);
              return (
                <div key={g.name} className="flex items-center gap-3">
                  <div className="relative">
                    <div
                      className="flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold"
                      style={{
                        borderColor: current || completed ? color : '#CBD5E1',
                        background: completed ? `${color}22` : current ? `${color}2E` : '#F8FAFC',
                        color: current || completed ? color : '#64748B'
                      }}
                    >
                      {completed ? '✓' : current ? '🐬' : '•'}
                    </div>
                    {current && <div className="absolute inset-[-5px] rounded-full border-2" style={{ borderColor: `${color}66` }} />}
                  </div>
                  <div className="flex-1">
                    <div className="font-finley-display text-base" style={{ color: current || completed ? '#0F172A' : '#64748B' }}>{g.name}</div>
                    <div className="text-xs text-finley-text-light">Levels {g.minLevel}-{g.maxLevel}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {beat4 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <p className="mb-2 text-sm leading-6 text-finley-text-light">
            At <strong className="text-finley-text">{levelName}</strong>, your child is learning to {levelConfig?.parentDescription || 'build strong reading skills'}.
          </p>
          <p className="mb-4 text-sm leading-6 text-finley-text-light">
            {nextGroup
              ? `After a few sessions, your child will unlock ${nextGroup.name}!`
              : 'Your child is at the highest level. Finley will keep challenging them with advanced content!'}
          </p>

          <motion.button {...tapBounce} onClick={onContinue} className="min-h-[52px] w-full rounded-finley-lg bg-finley-primary px-6 py-3 font-finley-display text-base text-white shadow-finley-md">
            {ctaMode === 'dashboard' ? 'Back to Dashboard' : 'Start your first adventure'}
          </motion.button>

          <button onClick={() => setWhyOpen((v) => !v)} className="mt-4 text-sm text-finley-text-light underline decoration-transparent underline-offset-2 transition hover:decoration-finley-text-light">
            Why this level?
          </button>
          {whyOpen && (
            <div className="mt-3 rounded-finley-lg border border-finley-text-muted/20 bg-finley-bg-card p-4 text-sm leading-6 text-finley-text-light">
              Finley's placement quiz adapts to your child's responses in real time. The starting level is designed to build confidence while Finley continues learning your child's strengths. The path adjusts automatically as your child plays. You can also adjust the level anytime in Settings.
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}
